import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { track } from "@/lib/analytics";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowRight01Icon,
  Add01Icon,
  Cancel01Icon,
  ArrowDown01Icon,
  Tick01Icon,
  LockPasswordIcon,
  GoogleGeminiIcon,
  ChatGptIcon,
  ClaudeIcon,
  Grok02Icon,
} from "@hugeicons/core-free-icons";
import { AnimatedShinyText } from "@/components/ui/animated-shiny-text";
import { AssetPickerSheet } from "@/components/assets/AssetPickerSheet";
import { createAssetsApi } from "@/lib/api/assets";
import type { Asset } from "@/lib/types/assets";
import type { ChatAttachmentImageRef } from "@/lib/api/chat";
import { createProjectsApi } from "@/lib/api/projects";
import type { AgentMode } from "@/components/project/ModeSelector";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type ModelId =
  | "best"
  | "gemini-3-flash"
  | "gemini-3.1-pro"
  | "gpt-5.2"
  | "claude-sonnet-4-6"
  | "claude-opus-4-6"
  | "grok-4.1";

interface ModelOption {
  id: ModelId;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  badge?: "New" | "Max";
  locked?: boolean;
}

const MODELS: ModelOption[] = [
  { id: "gemini-3-flash", label: "Gemini 3 Flash", icon: GoogleGeminiIcon },
  {
    id: "gemini-3.1-pro",
    label: "Gemini 3.1 Pro",
    icon: GoogleGeminiIcon,
    badge: "New",
  },
  { id: "gpt-5.2", label: "GPT-5.2", icon: ChatGptIcon },
  {
    id: "claude-sonnet-4-6",
    label: "Claude Sonnet 4.6",
    icon: ClaudeIcon,
    badge: "New",
  },
  {
    id: "claude-opus-4-6",
    label: "Claude Opus 4.6",
    icon: ClaudeIcon,
    badge: "Max",
    locked: true,
  },
  { id: "grok-4.1", label: "Grok 4.1", icon: Grok02Icon },
];

const MODES: { value: AgentMode; label: string; description: string }[] = [
  { value: "auto-detect", label: "Auto", description: "Let Rayloop decide the best approach" },
  { value: "auto", label: "Fast", description: "Build immediately without planning" },
  { value: "plan", label: "Plan", description: "Review a structured plan before building" },
  { value: "brainstorm", label: "Brainstorm", description: "Explore ideas interactively first" },
];

export function PromptInput() {
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState<AgentMode>("auto-detect");
  const [modeOpen, setModeOpen] = useState(false);
  const [model, setModel] = useState<ModelId>("best");
  const [modelOpen, setModelOpen] = useState(false);
  const [attachmentPickerOpen, setAttachmentPickerOpen] = useState(false);
  const [selectedAttachmentImage, setSelectedAttachmentImage] =
    useState<ChatAttachmentImageRef | null>(null);
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const assetsApi = useMemo(() => createAssetsApi({ getToken }), [getToken]);
  const projectsApi = createProjectsApi({ getToken });
  const [selectedAttachmentPreviewUrl, setSelectedAttachmentPreviewUrl] =
    useState<string | null>(null);

  useEffect(() => {
    const pending = sessionStorage.getItem("rayloop_pending_prompt");
    if (pending) {
      setPrompt(pending);
      sessionStorage.removeItem("rayloop_pending_prompt");
    }
  }, []);

  const selectedModel = MODELS.find((m) => m.id === model);
  const triggerLabel =
    model === "best" ? "Model" : (selectedModel?.label ?? "Model");
  const attachmentSizeLabel = useMemo(() => {
    const sizeBytes = selectedAttachmentImage?.sizeBytes;
    if (!sizeBytes || sizeBytes <= 0) return "Image";
    const kb = 1024;
    const mb = kb * 1024;
    if (sizeBytes >= mb) return `${(sizeBytes / mb).toFixed(1)} MB`;
    if (sizeBytes >= kb) return `${(sizeBytes / kb).toFixed(1)} KB`;
    return `${sizeBytes} B`;
  }, [selectedAttachmentImage?.sizeBytes]);

  const createProjectMutation = useMutation({
    mutationFn: (vars: {
      prompt: string;
      mode: AgentMode;
      model: string;
      attachmentImage: ChatAttachmentImageRef | null;
    }) =>
      projectsApi.create({
        name: "New Project",
        description: vars.prompt,
        message: vars.prompt,
        attachmentImage: vars.attachmentImage,
        agentMode: vars.mode,
        model: vars.model,
      }),
    onSuccess: (newProject) => {
      const initialAttachmentImage = selectedAttachmentImage;
      const initialMode = mode;
      track("project_created", { project_id: newProject.id });
      queryClient.setQueryData(["project", String(newProject.id)], newProject);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setPrompt("");
      setSelectedAttachmentImage(null);
      navigate(`/projects/${newProject.id}`, {
        state: {
          ...(initialAttachmentImage ? { initialAttachmentImage } : {}),
          ...(initialMode !== "auto-detect" ? { initialMode } : {}),
        },
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    createProjectMutation.mutate({
      prompt: prompt.trim(),
      mode,
      model,
      attachmentImage: selectedAttachmentImage,
    });
  };

  const handleSelectAttachmentImage = (asset: Asset) => {
    setSelectedAttachmentImage({
      assetId: asset.id,
      storageKey: asset.storageKey,
      name: asset.name,
      mimeType: asset.mimeType,
      sizeBytes: asset.sizeBytes,
    });
    setAttachmentPickerOpen(false);
  };

  useEffect(() => {
    if (!selectedAttachmentImage?.assetId) {
      setSelectedAttachmentPreviewUrl(null);
      return;
    }

    let objectUrl: string | null = null;
    assetsApi
      .fetchContent(selectedAttachmentImage.assetId)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setSelectedAttachmentPreviewUrl(objectUrl);
      })
      .catch(() => setSelectedAttachmentPreviewUrl(null));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setSelectedAttachmentPreviewUrl(null);
    };
  }, [assetsApi, selectedAttachmentImage?.assetId]);

  return (
    <div
      data-tour="prompt-input"
      className="relative z-10 flex w-full max-w-2xl flex-col items-center gap-6 px-6"
    >
      {/* Announcement badge */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="border-border/50 bg-card/60 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 backdrop-blur">
          <AnimatedShinyText className="text-xs sm:text-sm">
            New &middot; Introducing Rayloop AI &rarr;
          </AnimatedShinyText>
        </div>
      </motion.div>

      {/* Heading */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="font-display text-foreground text-center text-4xl leading-[1.1] font-normal tracking-tight sm:text-5xl"
      >
        What should we build?
      </motion.h1>

      {/* Prompt input card */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="w-full"
      >
        <div className="group relative">
          {/* Glow effect on focus */}
          <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-[oklch(0.55_0.25_285_/_0.25)] via-transparent to-[oklch(0.55_0.25_285_/_0.25)] opacity-0 blur-sm transition-opacity duration-500 group-focus-within:opacity-100" />

          <div className="bg-card/80 border-border/50 relative flex flex-col rounded-2xl border shadow-lg backdrop-blur">
            {selectedAttachmentImage && (
              <div className="mx-3 mt-3 mb-0 flex w-fit max-w-[280px] items-center gap-2 rounded-md border border-border/60 bg-muted/50 px-1.5 py-1.5">
                <div className="flex size-10 items-center justify-center overflow-hidden rounded-md bg-muted/60">
                  {selectedAttachmentPreviewUrl ? (
                    <img
                      src={selectedAttachmentPreviewUrl}
                      alt=""
                      className="size-full object-cover"
                    />
                  ) : (
                    <HugeiconsIcon
                      icon={Add01Icon}
                      className="size-4 text-muted-foreground/60"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground/85 leading-tight">
                    {selectedAttachmentImage.name}
                  </p>
                  <p className="text-xs text-muted-foreground/80 leading-tight mt-0.5">
                    {attachmentSizeLabel}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedAttachmentImage(null)}
                  className="flex size-6 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <HugeiconsIcon icon={Cancel01Icon} className="size-3.5" />
                </button>
              </div>
            )}

            {/* Textarea */}
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what you want to create..."
              rows={3}
              autoFocus
              className="placeholder:text-muted-foreground/50 text-foreground w-full resize-none bg-transparent px-5 pt-4 pb-2 text-sm outline-none sm:text-base"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />

            {/* Bottom toolbar */}
            <div className="flex items-center justify-between px-3 pb-3">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setAttachmentPickerOpen(true)}
                  className="text-muted-foreground hover:text-foreground hover:bg-accent flex size-8 items-center justify-center rounded-lg transition-colors cursor-pointer"
                  title="Add image from assets"
                >
                  <HugeiconsIcon icon={Add01Icon} className="size-4" />
                </button>

                {/* Mode selector */}
                <Popover open={modeOpen} onOpenChange={setModeOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        "flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                        mode !== "auto-detect"
                          ? "bg-[oklch(0.55_0.25_285_/_0.12)] text-[oklch(0.55_0.25_285)]"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent",
                      )}
                    >
                      {MODES.find((m) => m.value === mode)?.label ?? "Mode"}
                      <HugeiconsIcon icon={ArrowDown01Icon} className="size-3" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-1.5 gap-px" align="start">
                    {MODES.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => { setMode(m.value); setModeOpen(false); }}
                        className="w-full rounded-lg px-3 py-2 text-left transition-colors hover:bg-accent cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <span className={cn(
                            "text-sm font-medium",
                            mode === m.value ? "text-[oklch(0.55_0.25_285)]" : "text-foreground",
                          )}>
                            {m.label}
                          </span>
                          {mode === m.value && (
                            <HugeiconsIcon icon={Tick01Icon} className="size-4 text-[oklch(0.55_0.25_285)]" />
                          )}
                        </div>
                        <p className="text-muted-foreground mt-0.5 text-xs">{m.description}</p>
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>

                {/* Model selector */}
                <Popover open={modelOpen} onOpenChange={setModelOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground hover:bg-accent flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors cursor-pointer"
                    >
                      {triggerLabel}
                      <HugeiconsIcon
                        icon={ArrowDown01Icon}
                        className="size-3"
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-1.5 gap-px" align="start">
                    {/* Best option */}
                    <button
                      type="button"
                      onClick={() => {
                        setModel("best");
                        setModelOpen(false);
                      }}
                      className="w-full rounded-lg px-3 py-2 text-left transition-colors hover:bg-accent cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={cn(
                            "text-sm font-semibold",
                            model === "best"
                              ? "text-[oklch(0.55_0.25_285)]"
                              : "text-foreground",
                          )}
                        >
                          Best
                        </span>
                        {model === "best" && (
                          <HugeiconsIcon
                            icon={Tick01Icon}
                            className="size-4 text-[oklch(0.55_0.25_285)]"
                          />
                        )}
                      </div>
                      <p className="text-muted-foreground mt-0.5 text-xs">
                        Selects your best available models
                      </p>
                    </button>

                    <div className="border-border border-t my-1.5 mx-3" />

                    {/* Model list */}
                    {MODELS.map((m) => {
                      const button = (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => {
                            if (!m.locked) {
                              setModel(m.id);
                              setModelOpen(false);
                            }
                          }}
                          className={cn(
                            "flex w-full items-center justify-between gap-2.5 rounded-lg px-3 py-1.5 text-sm transition-colors",
                            m.locked
                              ? "cursor-pointer hover:bg-accent/50"
                              : "cursor-pointer hover:bg-accent",
                            model === m.id && !m.locked
                              ? "text-[oklch(0.55_0.25_285)]"
                              : "text-foreground",
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <HugeiconsIcon
                              icon={m.icon}
                              className={cn(
                                "size-4 shrink-0",
                                m.locked && "opacity-40",
                              )}
                            />
                            <span
                              className={cn(
                                "flex-1 text-left",
                                m.locked && "opacity-40",
                              )}
                            >
                              {m.label}
                            </span>
                            {m.badge && (
                              <span
                                className={cn(
                                  "rounded px-1.5 py-0.5 text-[10px] font-medium",
                                  "bg-[oklch(0.55_0.25_285_/_0.12)] text-[oklch(0.55_0.25_285)]",
                                )}
                              >
                                {m.badge}
                              </span>
                            )}
                          </div>
                          {m.locked ? (
                            <HugeiconsIcon
                              icon={LockPasswordIcon}
                              className="text-muted-foreground opacity-60 size-3.5"
                            />
                          ) : model === m.id ? (
                            <HugeiconsIcon
                              icon={Tick01Icon}
                              className="size-4 shrink-0 text-[oklch(0.55_0.25_285)]"
                            />
                          ) : null}
                        </button>
                      );

                      if (!m.locked) {
                        return button;
                      }

                      return (
                        <TooltipProvider key={m.id} delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild>{button}</TooltipTrigger>
                            <TooltipContent
                              side="right"
                              align="start"
                              sideOffset={6.1}
                              hideArrow
                              className="bg-card text-foreground border-border/50 z-100! max-w-[260px] rounded-xl border p-3 shadow-xl"
                            >
                              <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="bg-foreground text-background rounded-[8px] px-2 py-0.5 text-[11px] font-bold tracking-wide">
                                    Max
                                  </span>
                                  <span className="text-sm font-semibold">
                                    Access the top AI models
                                  </span>
                                </div>
                                <p className="text-muted-foreground text-[13px] leading-relaxed">
                                  Unlock access to the most powerful AI models
                                  by{" "}
                                  <a
                                    href="#"
                                    className="text-[oklch(0.55_0.25_285)] font-medium hover:underline"
                                  >
                                    upgrading your plan
                                  </a>{" "}
                                  today
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </PopoverContent>
                </Popover>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={!prompt.trim() || createProjectMutation.isPending}
                className={`flex size-8 items-center justify-center rounded-xl transition-colors cursor-pointer ${
                  prompt.trim() && !createProjectMutation.isPending
                    ? "bg-[oklch(0.55_0.25_285)] text-white hover:bg-[oklch(0.50_0.25_285)]"
                    : "bg-muted text-muted-foreground cursor-not-allowed"
                }`}
              >
                {createProjectMutation.isPending ? (
                  <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <HugeiconsIcon icon={ArrowRight01Icon} className="size-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.form>
      <AssetPickerSheet
        open={attachmentPickerOpen}
        onOpenChange={setAttachmentPickerOpen}
        acceptType="image"
        onSelect={handleSelectAttachmentImage}
      />
    </div>
  );
}
